import React, { useState, useEffect, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, FlatList, TextInput } from 'react-native';
import { DesktopAdminLayout } from '@/components/common/admin/desktop/DesktopAdminLayout';
import { DesktopPageHeader } from '@/components/common/admin/desktop/DesktopPageHeader';
import { LoadingSpinner, Accordion } from '@/components';
import { useLazyListAllFeatureFlagsQuery } from '@/store/api/admin/featureFlagsApi';
import { FeatureFlagAttributes } from '@/types/admin/featureFlags';
import { ADMIN_COLORS } from '@/constants/admin';
import { SVG_COMPONENTS } from '@/assets/admin';
import { DesktopFeatureFlagDetailsPanel } from './DesktopFeatureFlagDetailsPanel';
import { useFeatureFlagsData } from '../hooks/useFeatureFlagsData';
import { useOrganizationFlagsEditor } from '../hooks/useOrganizationFlagsEditor';
import { getOrgDotColor } from '../utils/featureFlagsUtils';
import { PropertyFlagRow } from '../components/PropertyFlagRow';

export const DesktopFeatureFlagsView: React.FC = () => {
  const [getFeatureFlags, { data, isLoading }] = useLazyListAllFeatureFlagsQuery();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFlag, setSelectedFlagLocal] = useState<FeatureFlagAttributes | null>(null);
  const [isOrganizationView, setIsOrganizationView] = useState(false);
  const [forceOpenIncrement, setForceOpenIncrement] = useState(0);

  // Use custom hooks for data management
  const { organizationSections } = useFeatureFlagsData({ data, searchTerm });

  const handleSelectRow = (flag: FeatureFlagAttributes, isOrgView: boolean) => {
    setSelectedFlagLocal(flag);
    setIsOrganizationView(isOrgView);
  };

  const { handleEditOrganization } = useOrganizationFlagsEditor(handleSelectRow);

  useEffect(() => {
    getFeatureFlags();
  }, [getFeatureFlags]);

  // Force open accordions when searching
  const hasActiveSearch = useMemo(() => !!searchTerm.trim(), [searchTerm]);

  useEffect(() => {
    if (hasActiveSearch) {
      setForceOpenIncrement(prev => prev + 1);
    } else {
      setForceOpenIncrement(0);
    }
  }, [hasActiveSearch]);

  const renderContent = () => {
    if (isLoading) {
      return (
        <View style={styles.loadingContainer}>
          <LoadingSpinner message="Loading feature flags..." />
        </View>
      );
    }

    if (!data?.response || data.response.length === 0) {
      return (
        <View style={styles.emptyContainer}>
          <Text style={styles.emptyTitle}>No Feature Flags</Text>
          <Text style={styles.emptySubtitle}>No feature flags have been created yet.</Text>
        </View>
      );
    }

    return (
      <FlatList
        style={styles.list}
        data={organizationSections}
        keyExtractor={(item) => item.orgName}
        renderItem={({ item }) => {
          const { orgName, flags } = item;
          const count = flags.length;
          const forceOpen = count > 0 ? forceOpenIncrement : undefined;

          return (
            <Accordion
              forceOpen={forceOpen}
              openWhen={hasActiveSearch && count > 0}
              closeWhen={!hasActiveSearch || (hasActiveSearch && count === 0)}
              header={(
                <View style={styles.orgHeaderLeft}>
                  <View style={[styles.orgDot, { backgroundColor: getOrgDotColor(orgName) }]} />
                  <Text style={styles.orgName}>{orgName}</Text>
                  <Text style={styles.orgCount}>{count}</Text>
                </View>
              )}
              headerRightComponent={(
                <TouchableOpacity
                  style={styles.editOrgButton}
                  onPress={() => handleEditOrganization(flags)}
                  hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                  activeOpacity={0.6}
                >
                  <SVG_COMPONENTS.PENCIL
                    width={16}
                    height={16}
                    color={ADMIN_COLORS.TEXT.PRIMARY}
                  />
                </TouchableOpacity>
              )}
            >
              {flags.map((flag) => (
                <PropertyFlagRow
                  key={`${flag.organization_id}-${flag.property_id || flag.id}`}
                  flag={flag}
                  onPress={() => handleSelectRow(flag, false)}
                />
              ))}
            </Accordion>
          );
        }}
        showsVerticalScrollIndicator={false}
      />
    );
  };

  return (
    <DesktopAdminLayout>
      <DesktopPageHeader
        title="Feature Flags"
        showAddButton={false}
      >
        <View style={styles.searchContainer}>
          <SVG_COMPONENTS.MAGNIFYING_GLASS
            width={16}
            height={16}
            color={ADMIN_COLORS.TEXT_TERTIARY}
            style={styles.searchIcon}
          />
          <TextInput
            style={styles.searchInput}
            placeholder="Search flags..."
            value={searchTerm}
            onChangeText={setSearchTerm}
            placeholderTextColor={ADMIN_COLORS.TEXT_TERTIARY}
          />
        </View>
      </DesktopPageHeader>

      <View style={styles.content}>
        {renderContent()}
      </View>

      <DesktopFeatureFlagDetailsPanel
        selectedFlag={selectedFlag}
        isOrganizationView={isOrganizationView}
        onClose={() => setSelectedFlagLocal(null)}
      />
    </DesktopAdminLayout>
  );
};

const styles = StyleSheet.create({
  content: {
    flex: 1,
    padding: 24,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    paddingHorizontal: 12,
    height: 40,
    minWidth: 280,
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    color: ADMIN_COLORS.TEXT.PRIMARY,
    outlineStyle: 'none',
  } as any,
  list: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT.SECONDARY,
    marginBottom: 8,
  },
  emptySubtitle: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT.SECONDARY,
  },
  orgHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  orgDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginRight: 12,
  },
  orgName: {
    fontSize: 18,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT.PRIMARY,
    marginRight: 12,
  },
  orgCount: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT.SECONDARY,
  },
  editOrgButton: {
    padding: 8,
    borderRadius: 4,
  },
});

